
import { Component, Inject, LOCALE_ID, forwardRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { distinctUntilChanged, map, merge, tap } from 'rxjs';

const HOUR12 = 12;
const HOUR24 = 24;

@Component({
    selector: 'tm-timepicker',
    templateUrl: './timepicker.component.html',
    styleUrls: ['./timepicker.component.scss'],
    imports: [
        MatFormFieldModule,
        MatSelectModule,
        ReactiveFormsModule
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TmTimepickerComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        class: 'tm-timepicker'
    }
})
export class TmTimepickerComponent implements ControlValueAccessor {
  private _value: string;
  protected isDisabled: boolean;

  protected get value() {
    return this._value;
  }

  protected set value(value) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  protected format: typeof HOUR12 | typeof HOUR24;

  protected hoursCtrl = new FormControl();
  protected HOURS: string[];
  protected minutesCtrl = new FormControl();
  protected minutesStep = 5;
  protected MINUTES = new Array(12).fill(0).map((_, i) => String(i*this.minutesStep).padStart(2, '0'));
  protected ampmCtrl = new FormControl<string>('');

  constructor(@Inject(LOCALE_ID) locale: string) {

    const format = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
    });
    const usedOptions = format.resolvedOptions();
    this.format = usedOptions.hour12 ? HOUR12 : HOUR24;

    this.HOURS = new Array(this.format).fill(0).map((_, i) => {
      // HOUR12 format: 1-12; HOUR24 format: 0-23
      const hour = this.format === HOUR12 ? i + 1 : i;
      return String(hour).padStart(2, '0')
    });

    merge(this.hoursCtrl.valueChanges, this.minutesCtrl.valueChanges, this.ampmCtrl.valueChanges).pipe(
      map(() => {
        const hours = this.hoursCtrl.value;
        const minutes = this.minutesCtrl.value;
        const ampm = this.ampmCtrl.value;

        let hours24: string;

        /**
         * 12AM is 00 and 12PM is 12
         */
        if (this.format === HOUR12) { 
          if (ampm === 'am') {
            if (hours === '12') {
              hours24 = '00';
            } else {
              hours24 = hours;
            }
          } else {
            if (hours === '12') {
              hours24 = '12';
            } else {
              hours24 = String(Number(hours) + 12);
            }
          }
        } else {
          hours24 = hours;
        }

        return `${hours24}:${minutes}`
      }),
      distinctUntilChanged(),
      tap(time => this.value = time),
      takeUntilDestroyed()
    ).subscribe()
  }
  
  writeValue(value: string): void {
    this._value = value;
    if (value) {
      this.propagateValueChanges();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  propagateValueChanges() {
    const hours24 = this.value.split(":")[0];

    // 00 is 12AM and 12 is 12PM
    let hoursNum: number;
    let ampm: 'am' | 'pm';

    if (this.format === HOUR12) {
      if (hours24 === '00' || hours24 === '12') {
        hoursNum = 12;
        ampm = hours24 === '00' ? 'am' : 'pm';
      } else {
        hoursNum = Number(hours24) % 12;
        ampm = Number(hours24) < 12 ? 'am' : 'pm';
      }
    } else {
      hoursNum = Number(hours24);
    }
    const hours = String(hoursNum).padStart(2, '0');

    const minutes = this.value.split(":")[1];

    this.hoursCtrl.setValue(hours, {emitEvent: false});
    this.minutesCtrl.setValue(minutes, { emitEvent: false });

    if (this.format === HOUR12) {
      this.ampmCtrl.setValue(ampm, { emitEvent: false });
    }
  }

}
